import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Box, Chip, FormGroup } from "@mui/material";
import { PrimaryButton } from "../../../../common/components/CustomButton";
import { If } from "../../../../common/components/If";
import { Tender } from "../../../tender_page/tender.module";
import { PricingStatus, Pricing, Action } from "../../pricingListSlice";
import useIsAuthorized from "../../../../common/hooks/useIsAuthorized";
import { PricingTransitions } from "../../pricingList.constant";
import { isUserAuthorizedToCreatePricings } from "../../../../common/authorization/tender.authorization";
import { TenderActionIcon } from "./TenderActionIcon";
import TuneIcon from "@mui/icons-material/Tune";
import { useAppDispatch } from "../../../../common/hooks/default";
import { Dispatch, useMemo } from "react";
import {
	setFiltersDrawerOpened,
	setPersistedFilters,
} from "../../../filters/filtersSlice";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { getPathWithParameters, PATH } from "../../../../router-path";
import {
	Column,
	ColumnsSelector,
} from "../../../../common/components/ColumnsSelector/ColumnsSelector";
import { ColumnIds } from "../../hooks/columns/common";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ButtonDropdown from "../../../../common/components/ButtonDropdown";
import { PricingStepperType } from "../../../pricing_request/hooks/usePricingStepper";
import { useSelector } from "react-redux";
import { selectPersistedFilters } from "../../../filters/filters.selector";
import useFetchFilters from "../../../../common/hooks/useFetchFilters";
import {
	buildPredicates,
	removeEmptyListsAndNullValues,
} from "../../../filters/utils";
import { CANCEL_FILTER } from "../../../filters/filters.constant";
import _ from "lodash";
import { CustomToggleButtonGroup } from "../../../../common/components/ToggleButtonGroup/ToggleButtonGroup";
import { DisplayMenu } from "./DisplayMenu";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";

interface TenderActionsProps {
	activeOnly: "all" | "active";
	setActiveOnly: (newValue: "all" | "active") => void;
	valueOrPercent: string;
	setValueOrPercent: (newValue: "value" | "percent") => void;
	canPrintUnderPricing: boolean;
	tender: Tender | undefined;
	pricings: Pricing[] | null | undefined;
	filterCount: number;
	selectedColumns: Column<ColumnIds>[];
	onColumnToggled: (column: Column<ColumnIds>) => void;
	availableColumns: Column<ColumnIds>[];
	actOnPricings: (action: Action) => void;
	onPricingsDownload: (
		tender_id: number,
		status_in: PricingStatus[],
		format: string,
		id_in?: number[] | null
	) => void;
	showHiddenRows: boolean;
	toggleShowHiddenRows: () => void;
}

const styles = {
	actionBar: {
		width: "100%",
		marginTop: "20px",
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		alignItems: "center",
		gap: "24px",
	},
	actionGroup: {
		display: "flex",
		flexDirection: "row",
		justifyContent: "flex-start",
		gap: "12px",
		alignItems: "center",
	},
};

export function TenderActions(props: TenderActionsProps) {
	const {
		activeOnly,
		setActiveOnly,
		valueOrPercent,
		setValueOrPercent,
		canPrintUnderPricing,
		tender,
		pricings,
		filterCount,
		selectedColumns,
		onColumnToggled,
		availableColumns,
		onPricingsDownload,
		actOnPricings,
		showHiddenRows,
		toggleShowHiddenRows,
	} = props;
	const dispatch: Dispatch<any> = useAppDispatch();
	const { t } = useTranslation();
	const { data: filtersData } = useFetchFilters("Pricings", tender?.id);
	const location = useLocation();
	const navigate = useNavigate();
	const canCreatePricings = useIsAuthorized<Tender>(
		isUserAuthorizedToCreatePricings,
		tender
	);
	const persistedFilters = useSelector(selectPersistedFilters);
	function showActiveOnly(choice: any) {
		if (choice !== null) {
			setActiveOnly(choice);
			const filters = JSON.parse(
				persistedFilters.filters?.pricings_filters || "{}"
			);
			if (choice === "active") {
				filters.pricings_status_not_in = filters.pricings_status_not_in
					? [...filters.pricings_status_not_in, CANCEL_FILTER]
					: [CANCEL_FILTER];
			} else {
				filters.pricings_status_not_in =
					filters.pricings_status_not_in.filter(
						(e: any) => e.id !== CANCEL_FILTER.id
					);
			}
			const { predicates } = buildPredicates(
				filtersData,
				filters,
				"Pricings"
			);
			const filteredValues = removeEmptyListsAndNullValues(filters);
			const odataString = `"${predicates.join(" and ")}"`;
			const filtersToPersist = JSON.stringify(filteredValues);
			const params = new URLSearchParams();
			params.set("filters", filtersToPersist);
			dispatch(
				setPersistedFilters({
					...persistedFilters.filters,
					pricings_filters: filtersToPersist,
					pricings_filters_count: predicates.length.toString(),
					pricings_query: odataString,
				})
			);
			navigate("?" + params.toString());
		}
	}

	const pricingSteppers = useMemo(() => {
		const steppers = [];
		if (!tender) {
			return [];
		}
		if (tender.actions_blotter.find((action: Action) => action.action === "CREATE_POWER_PRICING")) {
			steppers.push({ type: PricingStepperType.POWER_AND_GOO, key: "power", label: "Power" });
		}
		if (tender.actions_blotter.find((action: Action) => action.action === "CREATE_GOO_PRICING")) {
			steppers.push({ type: PricingStepperType.GOO_ONLY, key: "GOO Only", label: "pricing.product_family.goo_only" });
		}
		if (tender.actions_blotter.find((action: Action) => action.action === "CREATE_FINANCIAL_PRICING")) {
			steppers.push({ type: PricingStepperType.FINANCIAL, key: "financial", label: "Financial" });
		}

		return steppers;
	}, [tender]);

	return (
		<Box
			display="flex"
			justifyContent="space-between"
			sx={styles.actionBar}
		>
			<FormGroup sx={styles.actionGroup}>
				<CustomToggleButtonGroup
					groupValue={activeOnly}
					onValueChange={showActiveOnly}
					groupLabel="all-or-active-only"
					buttonValues={["all", "active"]}
					displayValues={["All", "Active only"]}
				/>
				<CustomToggleButtonGroup
					groupValue={valueOrPercent}
					onValueChange={setValueOrPercent}
					groupLabel="value-or-percentage"
					buttonValues={["value", "percent"]}
					displayValues={["Value", "%"]}
				/>
			</FormGroup>
			<FormGroup sx={styles.actionGroup}>
				<DisplayMenu
					tender={tender}
					availableColumns={availableColumns}
					selectedColumns={selectedColumns}
					onColumnToggled={onColumnToggled}
					showHiddenRows={showHiddenRows}
					toggleShowHiddenRows={toggleShowHiddenRows}
				/>
				<PrimaryButton
					text="Export Pricings"
					type="button"
					color="secondary"
					disabled={pricings?.length === 0}
					onClick={() => {
						onPricingsDownload(tender?.id ?? 0, [], valueOrPercent);
					}}
					startIcon={<DownloadIcon />}
				></PrimaryButton>
				<If condition={canPrintUnderPricing}>
					<PrimaryButton
						text="Export Under Offer"
						type="button"
						color="secondary"
						onClick={() => {
							onPricingsDownload(
								tender?.id || 0,
								[PricingStatus.UNDER_OFFER],
								valueOrPercent
							);
						}}
						startIcon={<DownloadIcon />}
					></PrimaryButton>
				</If>
				<PrimaryButton
					text="Filters"
					type="button"
					color="secondary"
					data-testid="open-filters"
					onClick={() =>
						dispatch(
							setFiltersDrawerOpened({
								opened: true,
							})
						)
					}
					startIcon={<TuneIcon />}
					endIcon={
						<Chip
							label={
								<div style={{ fontSize: "13px" }}>
									{filterCount}
								</div>
							}
							size="small"
						/>
					}
				/>
				<ColumnsSelector
					availableColumns={availableColumns}
					selectedColumns={selectedColumns}
					onColumnToggled={onColumnToggled}
				/>
				<PrimaryButton
					text="Pricing bricks description"
					type="button"
					role="link"
					href="https://confluence.tools.digital.engie.com/x/aq1LEw"
					color="secondary"
					sx={{
						marginRight: "10px",
						width: 230,
					}}
					target="_blank"
				>
					<HelpOutlineOutlinedIcon />
				</PrimaryButton>
			</FormGroup>
			<FormGroup sx={styles.actionGroup}>
				{(tender?.actions || []).map((action, index) => (
					<PrimaryButton
						key={index}
						text={action.display_name}
						type="submit"
						color="primary"
						onClick={() => {
							actOnPricings(action);
						}}
					>
						<TenderActionIcon
							transition={
								action.action as PricingTransitions
							}
						/>
					</PrimaryButton>
				))}
				{canCreatePricings && (
					<ButtonDropdown
						text="Add pricing"
						type="submit"
						color="primary"
						role="link"
						items={pricingSteppers.map((step) => (
							<Link
								key={step.key}
								to={`${getPathWithParameters(
									PATH.PRICING_REQUEST,
									{
										tenderId: tender?.id,
										type: _.kebabCase(
											step.type
										),
									}
								)}?backPage=${location.pathname}`}
							>
								{t(step.label)}
							</Link>
						))
}
					>
						<AddCircleIcon sx={{ color: "#fff" }} />
					</ButtonDropdown>
				)}
			</FormGroup>
		</Box>
	);
}
